import ReactDOM from 'react-dom';
import { useAxios } from '../utils/axiosConfig';
import { useNotification } from "./NotificationContext";
import { State, toODataString } from '@progress/kendo-data-query';
import React, { createContext, useContext, useState, useRef } from 'react';
// Define the OData context interface
interface ODataContextProps {
    data: any[];
    update: React.Dispatch<React.SetStateAction<any[]>>;
    total: number;
    loadData: (dataState: State, url?: string)  => Promise<any>;
    loading: boolean;
  }
const ODataContext = createContext<ODataContextProps>({} as ODataContextProps);
// Export a hook to easily use the OData context
export const useODataContext = (): ODataContextProps => {
    const context = useContext(ODataContext);
    if (!context) {
      throw new Error('useODataContext must be used within an ODataProvider');
    }
    return context;
  };
interface ODataProviderProps {
    baseUrl: string,   
    loadingContent: string,
    children: React.ReactNode
}
// Define the OData provider
export const ODataProvider:React.FC<ODataProviderProps> = ({ baseUrl, loadingContent, children }) => {
    const notifications = useNotification();    
    const axios = useAxios();
    
    const fullBaseUrl =baseUrl?(baseUrl.includes('?') ?  baseUrl + '&$count=true&' : baseUrl + '?$count=true&') : undefined;
    const init = { method: 'GET', accept: 'application/json', headers: {} };
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);
    const LoadingPanel = () => {
        const loadingPanel = (
          <div className="k-loading-mask">
            <span className="k-loading-text">Loading</span>
            <div className="k-loading-image" />
            <div className="k-loading-color" />
          </div>
        );
    
        const gridContent = document && document.querySelector(loadingContent);
        return gridContent ? ReactDOM.createPortal(loadingPanel, gridContent) : loadingPanel;
    };
    
    // Load data from the OData service
    const loadData = async (dataState: State, url: string | null = null) => {
        return new Promise((resolve) => { 
        const items: any[] = [];
        const apiUrl = url?(url.includes('?') ?  url + '&$count=true&' : url + '?$count=true&'):fullBaseUrl;
        if (apiUrl === undefined) return;
          
        setLoading(true);
        const myRequestId = ++requestIdRef.current;
        
        axios.get(apiUrl + toODataString(dataState), init)
            .then(response => {
                const json = response.data;
               
                if (myRequestId === requestIdRef.current) {
                    setData(json.value);
                    setTotal(json['@odata.count']);                   
                }               
                json.value.forEach((item: any, index: number) => {
                    items.push(item);                        
                });
                resolve(items);
            })
            .catch(error => {
                if (myRequestId === requestIdRef.current) {
                    notifications.showNotification("Error fetching OData: " + error, 'error');
                    console.error("Error fetching OData:", error);
                }
            })
            .finally(() => {
                if (myRequestId === requestIdRef.current) {
                    setLoading(false);
                }
            });
        }); 
    };
   
    return (
        <ODataContext.Provider value={{ data, update: setData, total, loadData, loading }}>
            {loading && <LoadingPanel />}
            {children}
        </ODataContext.Provider>
    );
};